import { useEffect, useState, useMemo } from "react";
import PromoVoucherModal, { Voucher } from "./PromoVoucherModal";
import { useNavigate } from "react-router-dom";
import { fetchVourcherList, quoteVouchers, reserveVouchers } from "../../api/Vourcher";
import { VoucherQuoteResponse, Vourcher } from "../../api/VourcherType";
import { ProductFull } from "./CartAction";
import { fetchGiftById, fetchGifts } from "../../api/Gift";
import { getUserPoints } from "../../api/points";
import PointsRewardModal, { RewardItem } from "./PointsRewardModal";
import { toast } from "react-toastify";
import { clearVoucherBooking, saveVoucherBooking } from "../../utils/checkoutVoucherBooking";
import { API_URL } from "env";

const TEMP_SHIPPING_FEE = 0;

interface CartSummarySidebarProps {
    totalQuantity: number;
    totalPrice: number;
    onCartChange?: () => void;
    selectedItems: ProductFull[];
}

export default function CartSummarySidebar({ totalQuantity, totalPrice, onCartChange, selectedItems }: CartSummarySidebarProps) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [openPointsModal, setOpenPointsModal] = useState(false);
    const [selectedVoucherIds, setSelectedVoucherIds] = useState<string[]>([]);
    const [selectedRewardIds, setSelectedRewardIds] = useState<string[]>([]);
    const [selectedVoucherRewardItems, setSelectedVoucherRewardItems] = useState<RewardItem[]>([]);
    const [selectedProductRewardItems, setSelectedProductRewardItems] = useState<RewardItem[]>([]);


    // State for cart calculations
    const [quoteStatus, setQuoteStatus] = useState<'idle' | 'loading' | 'error'>('idle');
    const [discountPrice, setDiscountPrice] = useState(0);
    const [finalPrice, setFinalPrice] = useState(0);
    const [pointEarned, setPointEarned] = useState(0);
    const [latestQuoteSnapshot, setLatestQuoteSnapshot] = useState<VoucherQuoteResponse | null>(null);
    const [quoteBreakdownMap, setQuoteBreakdownMap] = useState<Record<string, {
        success: boolean;
        message?: string;
        template_name?: string;
        discount_applied?: number;
        applied_to?: 'order' | 'shipping';
        type?: 'fixed' | 'percent' | 'ship' | 'gift';
        gift_id?: number;
        template_value?: number | string;
    }>>({});

    // Vouchers from API
    const [apiVouchers, setApiVouchers] = useState<Vourcher[]>([]);
    const [totalPoints, setTotalPoints] = useState(0);
    const [rewardItems, setRewardItems] = useState<RewardItem[]>([]);

    const loadVouchers = async () => {
        const res = await fetchVourcherList();
        if (res && res.status && res.data) {
            setApiVouchers(res.data.items);
            return res.data.items;
        }
        return [];
    };

    useEffect(() => {
        loadVouchers();
    }, []);

    useEffect(() => {
        const loadPointsAndRewards = async () => {
            const [pointsRes, giftsRes] = await Promise.all([
                getUserPoints(),
                Promise.all([
                    fetchGifts({ page: 1, limit: 50, type: 1 }),
                    fetchGifts({ page: 1, limit: 50, type: 2 }),
                ]),
            ]);

            if (pointsRes?.status && pointsRes.data) {
                setTotalPoints(Number(pointsRes.data.point_balance || 0));
            }

            const [productsRes, vouchersRes] = giftsRes;
            const productItems = productsRes?.status && productsRes.data?.items
                ? productsRes.data.items.map((item: any) => ({
                    id: String(item.id),
                    name: item.name || item.title || item.code || "Ưu đãi",
                    description:
                        item.describe ||
                        item.description ||
                        item.condition ||
                        item.note ||
                        "Đổi điểm nhận ưu đãi",
                    points: Number(item.point || item.point_cost || item.point_required || item.price || 0),
                    type: 1,
                    code: item.code || item.voucher_code,
                    quantity: item.quantity != null ? Number(item.quantity) : undefined,
                    is_out_of_stock: !!item.is_out_of_stock,
                }))
                : [];

            const voucherItems = vouchersRes?.status && vouchersRes.data?.items
                ? vouchersRes.data.items.map((item: any) => ({
                    id: String(item.id),
                    name: item.name || item.title || item.code || "Ưu đãi",
                    description:
                        item.describe ||
                        item.description ||
                        item.condition ||
                        item.note ||
                        "Đổi điểm nhận ưu đãi",
                    points: Number(item.point || item.point_cost || item.point_required || item.price || 0),
                    type: 2,
                    code: item.code || item.voucher_code,
                    quantity: item.quantity != null ? Number(item.quantity) : undefined,
                    is_out_of_stock: !!item.is_out_of_stock,
                }))
                : [];

            const nextRewards = [...voucherItems, ...productItems];
            if (nextRewards.length > 0) {
                setRewardItems(nextRewards);
            }
        };

        loadPointsAndRewards();
    }, []);

    // Calculate discount using quote API (multi voucher)
    useEffect(() => {
        const runQuote = async () => {
            setQuoteStatus('loading');
            const selectedCodesFromIds = apiVouchers
                .filter(item => selectedVoucherIds.includes(String(item.id)))
                .map(item => item.code);

            const selectedCodes = Array.from(new Set(selectedCodesFromIds));

            const selectedGiftIds = Array.from(new Set(
                selectedRewardIds
                    .map((selectionKey) => Number(selectionKey.split(":")[1]))
            ))
                .filter((giftId) => Number.isFinite(giftId) && giftId > 0);

            if (selectedItems.length === 0) {
                setDiscountPrice(0);
                setPointEarned(0);
                setQuoteBreakdownMap({});
                setLatestQuoteSnapshot(null);
                setQuoteStatus('idle');
                return;
            }

            const quoteItems = selectedItems.map(item => ({
                product_id: item.id,
                quantity: item.qty,
            }));

            const res = await quoteVouchers(selectedCodes, TEMP_SHIPPING_FEE, quoteItems, selectedGiftIds);
            setLatestQuoteSnapshot(res);
            setPointEarned(res.data?.point_earned || 0);

            if (selectedCodes.length > 0 || selectedGiftIds.length > 0) {
                const nextBreakdownMap: Record<string, {
                    success: boolean;
                    message?: string;
                    template_name?: string;
                    discount_applied?: number;
                    template_value?: number | string;
                    applied_to?: 'order' | 'shipping';
                    type?: 'fixed' | 'percent' | 'ship' | 'gift';
                    gift_id?: number;
                }> = {};
                if (res.data?.breakdown?.length) {
                    res.data.breakdown.forEach(item => {
                        const breakdownValue = {
                            success: !!item.success,
                            message: item.message,
                            template_name: item.template_name,
                            discount_applied: item.discount_applied,
                            template_value: item.template_value,
                            applied_to: item.applied_to,
                            type: item.type,
                            gift_id: item.gift_id,
                        };

                        if (item.code) {
                            nextBreakdownMap[item.code] = breakdownValue;
                        }

                        if (item.gift_id) {
                            nextBreakdownMap[`gift_${item.gift_id}`] = breakdownValue;
                        }
                    });
                }
                setQuoteBreakdownMap(nextBreakdownMap);

                if (res.success && res.data) {
                    setDiscountPrice(res.data.total_discount || 0);
                    setQuoteStatus('idle');
                } else {
                    setQuoteStatus('error');
                    if (res.message) {
                        toast.error(`Áp dụng voucher thất bại: ${res.message}`, {
                            position: "bottom-right",
                        });
                    }
                    setDiscountPrice(0);
                }
            } else {
                setDiscountPrice(0);
                setQuoteBreakdownMap({});
                setQuoteStatus('idle');
            }
        };

        runQuote();
    }, [selectedVoucherIds, totalPrice, selectedItems, apiVouchers, selectedRewardIds]);

    // Product-level discount (promo / sale price vs original price)
    const productDiscount = useMemo(() => {
        return selectedItems.reduce((sum, item) => {
            const original = Number(String(item.originalPrice).replace(/\./g, '').replace(/,/g, '')) || 0;
            const diff = original - item.rawPrice;
            if (diff > 0) {
                return sum + diff * item.qty;
            }
            return sum;
        }, 0);
    }, [selectedItems]);

    const totalDiscount = productDiscount + discountPrice;

    useEffect(() => {
        // Final price calculation
        const final = Math.max(0, totalPrice - discountPrice);
        setFinalPrice(final);
    }, [totalPrice, discountPrice]);

    const modalVouchers: Voucher[] = useMemo(() => {
        return apiVouchers.map(v => ({
            id: String(v.id),
            title: v.name || v.code,
            desc: v.describe || `Giảm ${v.value}${v.type === 1 ? '%' : 'đ'}`,
            code: v.code,
            exp_date: v.exp_date,
            disabled: totalPrice < v.min_amount
        }));
    }, [apiVouchers, totalPrice]);

    // Handle removing a voucher
    const removeVoucher = (id: string) => {
        setSelectedVoucherIds(prev => prev.filter(vid => vid !== id));
    };

    const removePointVoucherReward = (id: string) => {
        setSelectedVoucherRewardItems(prev => prev.filter((item) => item.id !== id));
        setSelectedRewardIds(prev => prev.filter((rewardId) => rewardId !== `2:${id}`));
    };

    const removePointProductReward = (id: string) => {
        setSelectedProductRewardItems(prev => prev.filter((item) => item.id !== id));
        setSelectedRewardIds(prev => prev.filter((rewardId) => rewardId !== `1:${id}`));
    };

    const handleApplyRewards = async (ids: string[]) => {
        setSelectedRewardIds(ids);

        const selectedRewards = rewardItems.filter((item) => ids.includes(`${item.type}:${item.id}`));
        const selectedVoucherRewards = selectedRewards.filter((item) => item.type === 2);
        const selectedProductRewards = selectedRewards.filter((item) => item.type === 1);

        setSelectedVoucherRewardItems(selectedVoucherRewards);

        const selectedProductRewardsWithDetail: RewardItem[] = await Promise.all(
            selectedProductRewards.map(async (item) => {
                const detailRes = await fetchGiftById(item.id);
                if (!detailRes?.status || !detailRes.data) {
                    return item;
                }

                const detailData = detailRes.data as any;
                return {
                    ...item,
                    name: detailData.name || detailData.title || item.name,
                    description:
                        detailData.describe ||
                        detailData.description ||
                        detailData.condition ||
                        detailData.note ||
                        item.description,
                    points: Number(detailData.point || detailData.point_cost || detailData.point_required || item.points || 0),
                    image: detailData.image || detailData.avatar || detailData.thumbnail || item.image,
                };
            })
        );

        setSelectedProductRewardItems(selectedProductRewardsWithDetail);

        const pointsRes = await getUserPoints();
        if (pointsRes?.status && pointsRes.data) {
            setTotalPoints(Number(pointsRes.data.point_balance || 0));
        }

    };

    return (
        <div className="MuiGrid-root MuiGrid-item MuiGrid-grid-sm-12 MuiGrid-grid-md-3 ctrl-mobile-total">
            <div className="styles_sidebarSticky__Kfyvt">
                <div className="styles_cartInfo__0SA1a">

                    <h3 className="styles_cartInfoHeading__XC2KU">Giỏ hàng</h3>

                    {/* Top numbers */}
                    <div className="styles_cartTop__8bSkd">
                        <div className="styles_cartItem__9XQUj">
                            <span className="styles_cartItemLabel__eFnz4">Số lượng</span>
                            <p className="styles_cartItemNumber__gEPCN">{totalQuantity}</p>
                        </div>

                        <div className="styles_cartItem__9XQUj">
                            <span className="styles_cartItemLabel__eFnz4">Tạm tính</span>
                            <p className="styles_cartItemNumber__gEPCN">{totalPrice.toLocaleString('vi-VN')}đ</p>
                        </div>

                        {pointEarned > 0 && (
                            <div className="styles_cartItem__9XQUj">
                                <span className="styles_cartItemLabel__eFnz4">Điểm tích lũy</span>
                                <p className="styles_cartItemNumber__gEPCN" style={{ color: '#e88438' }}>+{pointEarned.toLocaleString('vi-VN')}</p>
                            </div>
                        )}
                    </div>

                    {/* Discount select bar */}
                    <div className="styles_cartBody__HwEuR">

                        <div role="button" className="styles_cartDiscountInput__FyhpR" onClick={() => setOpenPointsModal(true)}>
                            <div>
                                <p className="styles_cartDiscountInputText__k7za6" style={{ marginBottom: 2, fontSize: 12, color: "#6b7280" }}>
                                    Tổng điểm: {totalPoints.toLocaleString('vi-VN')}
                                </p>
                                <p className="styles_cartDiscountInputText__k7za6">
                                    Đổi điểm lấy ưu đãi
                                </p>
                            </div>

                            <div className="styles_cartDiscountIcon__qAHa7">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M5.52729 3.52851C5.78764 3.26817 6.20975 3.26817 6.47011 3.52851L10.4701 7.52852C10.7304 7.78885 10.7304 8.21099 10.4701 8.47132L6.47011 12.4713C6.20975 12.7317 5.78764 12.7317 5.52729 12.4713C5.26694 12.211 5.26694 11.7889 5.52729 11.5285L9.0559 7.99992L5.52729 4.47133C5.26694 4.21097 5.26694 3.78887 5.52729 3.52851Z"
                                        fill="#E88438"
                                    />
                                </svg>
                            </div>
                        </div>

                        <div role="button" className="styles_cartDiscountInput__FyhpR" onClick={() => setOpen(true)} >
                            <p className="styles_cartDiscountInputText__k7za6">
                                Chọn hoặc nhập mã giảm giá
                            </p>

                            <div className="styles_cartDiscountIcon__qAHa7">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M5.52729 3.52851C5.78764 3.26817 6.20975 3.26817 6.47011 3.52851L10.4701 7.52852C10.7304 7.78885 10.7304 8.21099 10.4701 8.47132L6.47011 12.4713C6.20975 12.7317 5.78764 12.7317 5.52729 12.4713C5.26694 12.211 5.26694 11.7889 5.52729 11.5285L9.0559 7.99992L5.52729 4.47133C5.26694 4.21097 5.26694 3.78887 5.52729 3.52851Z"
                                        fill="#E88438"
                                    />
                                </svg>
                            </div>
                        </div>

                        {/* discount total */}
                        <div className="styles_cartItem__9XQUj hidden-1199">
                            <span className="styles_cartItemLabel__eFnz4">Tổng giảm giá</span>
                            <p className="styles_cartItemNumber__gEPCN">
                                {discountPrice > 0 ? `-${discountPrice.toLocaleString('vi-VN')}đ` : '0 đ'}
                            </p>
                        </div>

                        {/* applied voucher list */}
                        {selectedVoucherIds.map(id => {
                            const v = apiVouchers.find(item => String(item.id) === id);
                            if (!v) return null;
                            const breakdown = quoteBreakdownMap[v.code];
                            const isFailed = breakdown ? !breakdown.success : false;
                            return (
                                <div key={v.id} className="MuiGrid-root styles_cartDiscount__RJYT_ MuiGrid-item">
                                    <div className="styles_cartDiscountItem__8Ddi3">
                                        <div className="styles_cartDiscountInner__b2Oh0">
                                            <svg
                                                className="MuiSvgIcon-root styles_cartDiscountIcon__qAHa7 MuiSvgIcon-fontSizeSmall"
                                                viewBox="0 0 24 24"
                                                style={{ color: "rgb(47, 84, 235)" }}
                                            >
                                                <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58s1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41s-.23-1.06-.59-1.42zM13 20.01L4 11V4h7v-.01l9 9-7 7.02z" />
                                                <circle cx="6.5" cy="6.5" r="1.5" />
                                            </svg>

                                            <p
                                                className="MuiTypography-root styles_cartDiscountText__Lw6iE styles_noWrap__8pTB8 MuiTypography-body1"
                                                style={{ color: isFailed ? "#d4323b" : "rgb(47, 84, 235)", cursor: "pointer" }}
                                                title={v.code}
                                            >
                                                {v.code}
                                            </p>

                                            {breakdown && (
                                                <div style={{ marginLeft: 8, fontSize: 12, color: isFailed ? "#d4323b" : "#333" }}>
                                                    {breakdown.success
                                                        ? `${breakdown.template_name || 'Voucher hợp lệ'}`
                                                        : (breakdown.message || 'Voucher không khả dụng')}
                                                </div>
                                            )}

                                            {/* delete voucher */}
                                            <button
                                                className="MuiButtonBase-root MuiIconButton-root"
                                                title="Xoá mã giảm giá"
                                                style={{ padding: 0, marginLeft: 10 }}
                                                type="button"
                                                onClick={() => removeVoucher(String(v.id))}
                                            >
                                                <span className="MuiIconButton-label">
                                                    <img
                                                        alt="icon del voucher"
                                                        src="https://cdn-web-next.thuocsi.vn/images/delete_voucher.svg"
                                                        style={{ width: '17px' }}
                                                    />
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {selectedVoucherRewardItems.map((reward) => {
                            const rewardGiftKey = `gift_${Number(reward.id)}`;
                            const breakdown = (reward.code ? quoteBreakdownMap[reward.code] : undefined) || quoteBreakdownMap[rewardGiftKey];
                            const isFailed = breakdown ? !breakdown.success : false;

                            return (
                                <div key={`gift-voucher-${reward.id}`} className="MuiGrid-root styles_cartDiscount__RJYT_ MuiGrid-item">
                                    <div className="styles_cartDiscountItem__8Ddi3">
                                        <div className="styles_cartDiscountInner__b2Oh0">
                                            <svg
                                                className="MuiSvgIcon-root styles_cartDiscountIcon__qAHa7 MuiSvgIcon-fontSizeSmall"
                                                viewBox="0 0 24 24"
                                                style={{ color: "rgb(47, 84, 235)" }}
                                            >
                                                <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58s1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41s-.23-1.06-.59-1.42zM13 20.01L4 11V4h7v-.01l9 9-7 7.02z" />
                                                <circle cx="6.5" cy="6.5" r="1.5" />
                                            </svg>

                                            <p
                                                className="MuiTypography-root styles_cartDiscountText__Lw6iE styles_noWrap__8pTB8 MuiTypography-body1"
                                                style={{ color: isFailed ? "#d4323b" : "rgb(47, 84, 235)", cursor: "pointer" }}
                                                title={reward.name}
                                            >
                                                {`Voucher đổi điểm: ${reward.name}`}
                                            </p>

                                            {breakdown && (
                                                <div style={{ marginLeft: 8, fontSize: 12, color: isFailed ? "#d4323b" : "#333" }}>
                                                    {breakdown.success
                                                        ? `${breakdown.template_name || 'Voucher hợp lệ'} • -${(breakdown.discount_applied || 0).toLocaleString('vi-VN')}đ • ${breakdown.applied_to === 'shipping' ? 'Shipping' : 'Order'} • ${breakdown.type || ''}`
                                                        : (breakdown.message || 'Voucher không khả dụng')}
                                                </div>
                                            )}

                                            <button
                                                className="MuiButtonBase-root MuiIconButton-root"
                                                title="Xoá mã giảm giá"
                                                style={{ padding: 0, marginLeft: 10 }}
                                                type="button"
                                                onClick={() => removePointVoucherReward(reward.id)}
                                            >
                                                <span className="MuiIconButton-label">
                                                    <img
                                                        alt="icon del voucher"
                                                        src="https://cdn-web-next.thuocsi.vn/images/delete_voucher.svg"
                                                        style={{ width: '17px' }}
                                                    />
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {selectedProductRewardItems.map((gift) => {
                            const giftKey = `gift_${Number(gift.id)}`;
                            const breakdown = quoteBreakdownMap[giftKey];
                            const isFailed = breakdown ? !breakdown.success : false;

                            return (
                                <div key={`gift-${gift.id}`} className="MuiGrid-root styles_cartDiscount__RJYT_ MuiGrid-item">
                                    <div className="styles_cartDiscountItem__8Ddi3">
                                        <div className="styles_cartDiscountInner__b2Oh0" style={{ alignItems: "flex-start" }}>
                                            {gift.image ? (
                                                <img src={gift.image} alt={gift.name} style={{ width: 32, height: 32, borderRadius: 6, objectFit: "cover", marginTop: 2 }} />
                                            ) : (
                                                <svg
                                                    className="MuiSvgIcon-root styles_cartDiscountIcon__qAHa7 MuiSvgIcon-fontSizeSmall"
                                                    viewBox="0 0 24 24"
                                                    style={{ color: isFailed ? "#d4323b" : "rgb(47, 84, 235)" }}
                                                >
                                                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z" />
                                                </svg>
                                            )}

                                            <div style={{ marginLeft: 8 }}>
                                                <p className="MuiTypography-root styles_cartDiscountText__Lw6iE MuiTypography-body1" style={{ margin: 0, color: isFailed ? "#d4323b" : "inherit" }}>
                                                    Quà: {gift.name}
                                                </p>
                                                <div style={{ marginTop: 2, fontSize: 12, color: isFailed ? "#d4323b" : "#6b7280" }}>
                                                    {breakdown && isFailed
                                                        ? (breakdown.message || "Quà đổi điểm không khả dụng")
                                                        : `${gift.description} • ${gift.points.toLocaleString('vi-VN')} điểm`}
                                                </div>
                                            </div>

                                            <button
                                                className="MuiButtonBase-root MuiIconButton-root"
                                                title="Xoá quà"
                                                style={{ padding: 0, marginLeft: 10 }}
                                                type="button"
                                                onClick={() => removePointProductReward(gift.id)}
                                            >
                                                <span className="MuiIconButton-label">
                                                    <img
                                                        alt="icon del gift"
                                                        src="https://cdn-web-next.thuocsi.vn/images/delete_voucher.svg"
                                                        style={{ width: '17px' }}
                                                    />
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* footer total */}
                    <div className="styles_cartFooter__i6d0j">

                        <div className="styles_cartItem__9XQUj">
                            <span className="styles_cartItemLabel__eFnz4 styles_bold__72Kns">
                                Tổng tiền
                            </span>

                            <p className="styles_cartItemNumber__gEPCN styles_primary__LIgRb">
                                {finalPrice.toLocaleString('vi-VN')}đ
                            </p>
                        </div>

                        <button
                            disabled={quoteStatus === 'loading' || selectedItems.length === 0 || Object.values(quoteBreakdownMap).some(b => !b.success) || quoteStatus === 'error'}
                            onClick={async () => {
                                const selectedVouchersFromIds = apiVouchers.filter(v => {
                                    if (!selectedVoucherIds.includes(String(v.id))) return false;
                                    const status = quoteBreakdownMap[v.code];
                                    return status ? status.success : true;
                                });

                                const successfulVoucherRewardIds = selectedVoucherRewardItems
                                    .filter((gift) => {
                                        const status = (gift.code ? quoteBreakdownMap[gift.code] : undefined)
                                            || quoteBreakdownMap[`gift_${Number(gift.id)}`];
                                        return status ? status.success : true;
                                    })
                                    .map((gift) => Number(gift.id))
                                    .filter((giftId) => Number.isFinite(giftId) && giftId > 0);

                                const successfulProductRewardIds = selectedProductRewardItems
                                    .filter((gift) => {
                                        const status = quoteBreakdownMap[`gift_${Number(gift.id)}`];
                                        return status ? status.success : true;
                                    })
                                    .map((gift) => Number(gift.id))
                                    .filter((giftId) => Number.isFinite(giftId) && giftId > 0);

                                const hasInvalidSelectedVoucher = apiVouchers.some(v => {
                                    if (!selectedVoucherIds.includes(String(v.id))) return false;
                                    const status = quoteBreakdownMap[v.code];
                                    return status ? !status.success : false;
                                });

                                const hasInvalidSelectedVoucherReward = selectedVoucherRewardItems.some((gift) => {
                                    const status = (gift.code ? quoteBreakdownMap[gift.code] : undefined)
                                        || quoteBreakdownMap[`gift_${Number(gift.id)}`];
                                    return status ? !status.success : false;
                                });

                                const hasInvalidSelectedProductReward = selectedProductRewardItems.some((gift) => {
                                    const status = quoteBreakdownMap[`gift_${Number(gift.id)}`];
                                    return status ? !status.success : false;
                                });

                                if (selectedItems.length === 0) {
                                    toast.error("Vui lòng chọn sản phẩm để mua hàng.", {
                                        position: "bottom-right",
                                    });
                                    return;
                                }

                                if (hasInvalidSelectedVoucher) {
                                    toast.warning("Có mã giảm giá không hợp lệ theo breakdown, hệ thống sẽ bỏ qua các mã đó khi thanh toán.", {
                                        position: "bottom-right",
                                    });
                                }

                                if (hasInvalidSelectedVoucherReward) {
                                    toast.warning("Có voucher đổi điểm không hợp lệ theo breakdown, hệ thống sẽ bỏ qua các voucher đó khi thanh toán.", {
                                        position: "bottom-right",
                                    });
                                }

                                if (hasInvalidSelectedProductReward) {
                                    toast.warning("Có quà đổi điểm không hợp lệ theo breakdown, hệ thống sẽ bỏ qua các quà đó khi thanh toán.", {
                                        position: "bottom-right",
                                    });
                                }

                                const reserveCodes = selectedVouchersFromIds.map((voucher) => voucher.code).filter(Boolean);
                                const reserveItems = selectedItems.map((item) => ({
                                    product_id: item.id,
                                    quantity: item.qty,
                                }));

                                if (reserveCodes.length > 0 && reserveItems.length > 0) {
                                    const reserveRes = await reserveVouchers(reserveCodes, TEMP_SHIPPING_FEE, reserveItems);

                                    if (!reserveRes.success || !reserveRes.data) {
                                        clearVoucherBooking();
                                        toast.error(reserveRes.message || "Không thể giữ chỗ voucher trước khi thanh toán.", {
                                            position: "bottom-right",
                                        });
                                        return;
                                    }

                                    saveVoucherBooking({
                                        codes: reserveCodes,
                                        items: reserveItems,
                                        bookingIds: reserveRes.data.reserved.map((item: { booking_id: number }) => item.booking_id),
                                        reservedCodes: reserveRes.data.reserved.map((item: { code: string }) => item.code),
                                        expiresAt: reserveRes.data.reserved
                                            .map((item: { expires_at: string }) => item.expires_at)
                                            .filter(Boolean)
                                            .sort()[0],
                                    });

                                    if (reserveRes.data.failed?.length > 0) {
                                        toast.warning(reserveRes.data.failed[0].message || "Một số voucher không giữ chỗ được.", {
                                            position: "bottom-right",
                                        });
                                    }
                                } else {
                                    clearVoucherBooking();
                                }

                                navigate("/checkout", {
                                    state: {
                                        selectedItems,
                                        selectedVouchers: selectedVouchersFromIds,
                                        selectedPointVoucherGiftIds: successfulVoucherRewardIds,
                                        selectedPointProductGiftIds: successfulProductRewardIds,
                                        quoteSnapshot: latestQuoteSnapshot,
                                    },
                                });
                            }} className="styles_button__l7oVR styles_base__Pdm9S styles_contained__4kDf4 styles_colorPrimary__NGf0C styles_large__aTxqv">
                            Mua hàng
                        </button>
                    </div>

                </div>
            </div>
            <PromoVoucherModal
                open={open}
                onClose={() => setOpen(false)}
                vouchers={modalVouchers}
                validateItems={selectedItems.map(item => ({
                    product_id: item.id,
                    quantity: item.qty,
                }))}
                quoteStatusMap={quoteBreakdownMap}
                defaultSelected={selectedVoucherIds}
                onSubmit={setSelectedVoucherIds}
                singleSelect={true}
            />
            <PointsRewardModal
                open={openPointsModal}
                onClose={() => setOpenPointsModal(false)}
                totalPoints={totalPoints}
                rewards={rewardItems}
                defaultSelected={selectedRewardIds}
                onApply={handleApplyRewards}
            />
        </div>
    );
}
